import React, {
  FC, useState, useEffect,
} from 'react';
import { connect } from 'react-redux';
import { IconButton, LinearProgress } from '@mui/material';
import {
  Close, Photo, Videocam, Audiotrack as Audio, Description, Inventory,
} from '@mui/icons-material';
import { round } from 'lodash';
import { DialogContentContainer, DialogHeader, DialogHeaderTitle } from '../../../Shared/styles/dialog';
import { IDispatchProps, IProps, IStateProps } from './interfaces';
import { IAppState } from '../../../../state/reducers';
import { clearStorageSummary, getStorageSummary } from '../../../../state/storageSummary';
import { ProgressContainer } from '../../../Shared/JobList/styles';
import {
  StorageData, StyledAssetCount, StyledStorageValue, StyledStorageTitle,
} from './styles';

const StorageSummary: FC<IProps> = (props: IProps) => {
  const { isLoading, onClose, storage } = props;
  const [isLoaded, setIsLoaded] = useState(true);

  useEffect(() => {
    const { onGetStorage, storage } = props;

    if (!storage && !isLoading) {
      onGetStorage();
      setIsLoaded(false);
    } else {
      setIsLoaded(true);
    }
  }, [isLoaded, isLoading, storage]);

  const calcStorageUnits = (value: number): string => {
    let _value = '';
    const kBytes = 1024;
    const mBytes = kBytes ** 2;
    const gBytes = kBytes ** 3;

    if (value >= gBytes) {
      const toGb = round(value / gBytes, 2);
      _value = `${toGb.toLocaleString()} GB`;
    } else if (value >= mBytes) {
      const toMb = round(value / mBytes, 2);
      _value = `${toMb.toLocaleString()} MB`;
    } else {
      const toKb = round(value / kBytes, 2);
      _value = `${toKb.toLocaleString()} KB`;
    }
    return _value;
  };

  const formatAssetCount = (count?: number): string => {
    const _count = count ?? 0;
    return `${_count.toLocaleString()} ${_count === 1 ? 'asset' : 'assets'}`;
  };

  return (
    <>
      <DialogHeader
        data-auto="StorageSummary"
      >
        <DialogHeaderTitle style={{ marginLeft: '12px' }}>
          Storage Summary
        </DialogHeaderTitle>
        <IconButton data-auto="CloseStorageSummary" onClick={onClose} size="large">
          <Close />
        </IconButton>
      </DialogHeader>
      <ProgressContainer>
        {isLoading && <LinearProgress />}
      </ProgressContainer>
      <DialogContentContainer style={{ display: 'flex', justifyContent: 'space-around', paddingBottom: '20px' }}>
        {storage && (
          <>
            <StorageData>
              <Videocam fontSize="large" />
              <StyledStorageTitle>
                Video
              </StyledStorageTitle>
              <StyledStorageValue>
                {calcStorageUnits(storage.storageAmountVideo)}
              </StyledStorageValue>
              <StyledAssetCount>
                {formatAssetCount(storage.assetCountVideo)}
              </StyledAssetCount>
            </StorageData>
            <StorageData>
              <Photo fontSize="large" />
              <StyledStorageTitle>
                Image
              </StyledStorageTitle>
              <StyledStorageValue>
                {calcStorageUnits(storage.storageAmountImage)}
              </StyledStorageValue>
              <StyledAssetCount>
                {formatAssetCount(storage.assetCountImage)}
              </StyledAssetCount>
            </StorageData>
            <StorageData>
              <Audio fontSize="large" />
              <StyledStorageTitle>
                Audio
              </StyledStorageTitle>
              <StyledStorageValue>
                {calcStorageUnits(storage.storageAmountAudio)}
              </StyledStorageValue>
              <StyledAssetCount>
                {formatAssetCount(storage.assetCountAudio)}
              </StyledAssetCount>
            </StorageData>
            <StorageData>
              <Description fontSize="large" />
              <StyledStorageTitle>
                Document
              </StyledStorageTitle>
              <StyledStorageValue>
                {calcStorageUnits(storage.storageAmountDocument)}
              </StyledStorageValue>
              <StyledAssetCount>
                {formatAssetCount(storage.assetCountDocument)}
              </StyledAssetCount>
            </StorageData>
            <StorageData>
              <Inventory fontSize="large" />
              <StyledStorageTitle>
                Misc
              </StyledStorageTitle>
              <StyledStorageValue>
                {calcStorageUnits(storage.storageAmountMisc)}
              </StyledStorageValue>
              <StyledAssetCount>
                {formatAssetCount(storage.assetCountMisc)}
              </StyledAssetCount>
            </StorageData>
          </>
        )}
      </DialogContentContainer>
    </>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  isLoading: state.storageSummary.isLoading,
  isOpen: state.storageSummary.isOpen,
  storage: state.storageSummary.storage,
});

const mapDispatchToProps: IDispatchProps = {
  onGetStorage: getStorageSummary,
  onClose: clearStorageSummary,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(StorageSummary);
